import { useState, useCallback } from "react";
import { showToast } from "../../shared";
import { HiOutlinePaperClip, HiOutlineDocument, HiOutlineEye, HiOutlineArrowDownTray, HiOutlineXMark } from "react-icons/hi2";
import FilePickerModal from "../FilePickerModal";
import FileUploadAndAttachModal from "../FileUploadAndAttachModal";
import { useFileAttachments, useAttachProjectFile, useDetachFileAttachment, useUploadAndAttachProjectFile } from "../../../lib/react-query/fileAttachments";
import { useFolderTree, useFileUrl, usePresignUpload } from "../../../lib/react-query/files";
import { extractBackendErrorMessage } from "../../../utils/utils";
import { cn } from "../../../lib/cn";
import { FileViewer } from "../FileViewer";

const TaskAttachmentSection = ({
  projectId,
  taskId,
  permissions,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);

  const [viewerFile, setViewerFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const { data: attachmentData } = useFileAttachments(
    projectId,
    { object_type: "TASK", object_id: Number(taskId) },
    !!projectId && !!taskId,
  );

  const { data: folderTreeData } = useFolderTree(projectId, null, pickerOpen || uploadModalOpen);
  const { mutateAsync: getFileUrl } = useFileUrl();
  const presignUpload = usePresignUpload();
  const attachMutation = useAttachProjectFile();
  const detachMutation = useDetachFileAttachment();
  const uploadAndAttach = useUploadAndAttachProjectFile();

  const attachments = attachmentData?.items || [];
  const count = attachmentData?.count ?? attachments.length;
  const attachmentPermissions = attachmentData?.permissions || permissions || {};
  const hasAttachments = count > 0;

  const uploadFileToS3 = useCallback(async (upload, file) => {
    const formData = new FormData();
    Object.entries(upload.fields || {}).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append("file", file);

    const response = await fetch(upload.upload_url, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed for ${file.name}`);
    }
  }, []);

  const handleAttach = useCallback(async (fileId) => {
    try {
      await attachMutation.mutateAsync({
        projectId,
        payload: {
          file_id: fileId,
          object_type: "TASK",
          object_id: Number(taskId),
          role: "CONTEXT",
        },
      });
      setPickerOpen(false);
      setSelectedFileId(null);
      showToast("File attached successfully.", "success");
    } catch (err) {
      showToast(extractBackendErrorMessage(err, "Failed to attach file"), "error");
    }
  }, [projectId, taskId, attachMutation]);

  const handleDetach = useCallback(async (attachment) => {
    try {
      await detachMutation.mutateAsync({
        projectId,
        attachmentId: attachment.id,
      });
      showToast("File detached.", "success");
    } catch (err) {
      showToast(extractBackendErrorMessage(err, "Failed to detach file"), "error");
    }
  }, [projectId, detachMutation]);

  const handlePreview = useCallback(async (attachment) => {
    try {
      const isPdf = attachment.file?.extension?.toLowerCase()?.replace(/^\./, "") === "pdf";
      if (isPdf) {
        const url = await getFileUrl({ projectId, fileId: attachment.file.id });
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        }
        return;
      }

      setViewerFile(attachment.file);
      setPreviewUrl(null);
      setPreviewError(null);
      const url = await getFileUrl({
        projectId,
        fileId: attachment.file.id,
      });
      if (url) {
        setPreviewUrl(url);
      }
    } catch (err) {
      setPreviewError(extractBackendErrorMessage(err, "Failed to load preview"));
      setPreviewUrl(null);
    }
  }, [getFileUrl, projectId]);

  const handleCloseViewer = useCallback(() => {
    setViewerFile(null);
    setPreviewUrl(null);
    setPreviewError(null);
  }, []);

  const handleDownload = useCallback(async (attachment) => {
    try {
      const url = await getFileUrl({
        projectId,
        fileId: attachment.file.id,
      });
      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.download = attachment.file.original_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      showToast("Failed to download file.", "error");
    }
  }, [getFileUrl, projectId]);

  const handleUploadSelection = useCallback(async ({ files, folderId }) => {
    if (!files?.length) return;

    try {
      const presigned = await presignUpload.mutateAsync({
        projectId,
        files: files.map((file) => ({
          original_name: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
        })),
        folderId: folderId ?? null,
      });

      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const upload = presigned?.files?.[index];
        if (!upload) {
          throw new Error(`Missing upload payload for ${file.name}`);
        }

        await uploadFileToS3(upload, file);
        await uploadAndAttach.mutateAsync({
          projectId,
          s3_key: upload.s3_key,
          original_name: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
          folder_id: upload.folder_id ?? folderId ?? null,
          attach_to: {
            object_type: "TASK",
            object_id: Number(taskId),
            role: "CONTEXT",
          },
        });
      }

      showToast("File uploaded and attached.", "success");
      setUploadModalOpen(false);
    } catch (err) {
      showToast(extractBackendErrorMessage(err, "Failed to upload and attach file"), "error");
    }
  }, [presignUpload, projectId, taskId, uploadAndAttach, uploadFileToS3]);

  return (
    <div className="flex flex-col gap-2">
      {/* Summary row or just actions */}
      {hasAttachments ? (
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary-dark hover:text-darkgrey transition-colors rounded-md px-1.5 py-0.5 -ml-1.5"
          >
            <HiOutlinePaperClip className="h-3.5 w-3.5 shrink-0" />
            <span>
              {count === 1 ? "1 file attached" : `${count} files attached`}
            </span>
            <svg
              className={cn("h-3 w-3 shrink-0 transition-transform duration-150", expanded && "rotate-180")}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </button>
          {attachmentPermissions.can_attach && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-secondary-dark transition-colors hover:bg-secondary/10"
              >
                Attach file
              </button>
              <button
                type="button"
                onClick={() => setUploadModalOpen(true)}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-secondary-dark transition-colors hover:bg-secondary/10"
              >
                Upload & attach
              </button>
            </div>
          )}
        </div>
      ) : attachmentPermissions.can_attach ? (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-secondary-dark transition-colors hover:bg-secondary/10"
          >
            Attach file
          </button>
          <button
            type="button"
            onClick={() => setUploadModalOpen(true)}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-secondary-dark transition-colors hover:bg-secondary/10"
          >
            Upload & attach
          </button>
        </div>
      ) : null}

      {/* Expanded compact file list */}
      {hasAttachments && expanded && (
        <div className="flex flex-col gap-1">
          {attachments.map((att) => {
            const isUnavailable = att.status === "UNAVAILABLE";

            return (
              <div key={att.id}>
                <div
                  className={cn(
                    "flex items-center gap-1.5 rounded-md border px-2 py-1",
                    isUnavailable ? "border-grey/15 bg-grey/[0.03]" : "border-grey/15 bg-white hover:border-grey/25",
                  )}
                >
                  <HiOutlineDocument className="h-3 w-3 shrink-0 text-grey/60" />
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className={cn("truncate text-xs", isUnavailable ? "text-grey line-through" : "text-darkgrey")}>
                      {att.file?.original_name || "Unknown file"}
                    </span>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {!isUnavailable && (
                      <>
                        <button
                          type="button"
                          onClick={() => handlePreview(att)}
                          className="rounded p-1 text-grey/60 hover:text-darkgrey transition-colors"
                          aria-label="Preview"
                        >
                          <HiOutlineEye className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownload(att)}
                          className="rounded p-1 text-grey/60 hover:text-darkgrey transition-colors"
                          aria-label="Download"
                        >
                          <HiOutlineArrowDownTray className="h-3 w-3" />
                        </button>
                      </>
                    )}
                    {attachmentPermissions.can_detach && (
                      <button
                        type="button"
                        onClick={() => handleDetach(att)}
                        className="rounded p-1 text-grey/60 hover:text-red transition-colors"
                        aria-label="Detach"
                      >
                        <HiOutlineXMark className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                {isUnavailable && (
                  <p className="text-[10px] text-grey/60 italic mt-0.5 ml-5">This file is no longer available.</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <FilePickerModal
        open={pickerOpen}
        onClose={() => { setPickerOpen(false); setSelectedFileId(null); }}
        tree={folderTreeData?.tree}
        selectedFileId={selectedFileId}
        onSelectFile={(file) => setSelectedFileId(file.id)}
        onAttach={handleAttach}
        isLoading={attachMutation.isPending}
        title="Attach file to task"
        attachLabel="Attach file"
      />

      <FileUploadAndAttachModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleUploadSelection}
        isLoading={presignUpload.isPending || uploadAndAttach.isPending}
        objectType="task"
        folderTree={folderTreeData?.tree}
      />

      <FileViewer
        file={viewerFile}
        previewUrl={previewUrl}
        previewError={previewError}
        isOpen={!!viewerFile}
        onClose={handleCloseViewer}
        onDownload={async () => {
          if (!viewerFile) return;
          try {
            const url = await getFileUrl({ projectId, fileId: viewerFile.id });
            if (url) {
              const a = document.createElement("a");
              a.href = url;
              a.download = viewerFile.original_name;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }
          } catch (err) {
            showToast("Failed to download file.", "error");
          }
        }}
        canShare={false}
      />
    </div>
  );
};

export default TaskAttachmentSection;
